<!--
  OrderProfitBreakdown -- DISTRIBUTOR-INTERNAL per-order profit panel.

  Card A1 (Pricing & Splits). Design: pricing-splits-engine sections 8.2/8.3
  ("Splits breakdown per order -- for any order, show the split rows in a
  friendly format"; "straight queries over order_split").

  Renders the cost / fees / payment / profit decomposition returned by
  GET /orders/:id/split. This is the SEPARATE distributor-only panel that the
  OrderDetailView header comment (Jake, 2026-06-30) called out as a follow-up:
  OrderDetailView stays client-safe (no cost/margin fields); THIS component is
  where the distributor-internal cost/margin/profit lives, so it must only ever
  be mounted on a distributor-context surface (never the client portal).

  Usage:
    - Embed with a pre-fetched breakdown:  <OrderProfitBreakdown :breakdown="b" />
    - Or let it self-fetch by order id:    <OrderProfitBreakdown :order-id="42" />

  Money is integer cents on the wire; formatted here via the shared currency
  helper (cents -> dollars -> "$1,234.56").
-->
<template>
  <div class="order-profit-breakdown" data-testid="order-profit-breakdown">
    <div class="opb-header">
      <h3 class="text-h6">Profit breakdown</h3>
      <span class="opb-internal-badge" data-testid="opb-internal-badge">
        Internal
      </span>
    </div>

    <div v-if="loading" class="opb-loading" data-testid="opb-loading">
      Loading breakdown&hellip;
    </div>

    <div v-else-if="error" class="opb-error" data-testid="opb-error">
      {{ error }}
    </div>

    <div v-else-if="model" class="opb-body">
      <!-- After a refund (card #696): the charge and what went back, so
           Charged - Refunded = Payment and the rows below still foot. -->
      <template v-if="hasRefunds">
        <div class="opb-row" data-testid="opb-charged">
          <span class="opb-label">Charged</span>
          <span class="opb-value">{{ money(model.charged_cents) }}</span>
        </div>
        <div class="opb-row" data-testid="opb-refunded">
          <span class="opb-label">Refunded</span>
          <span class="opb-value">{{ money(-model.refunded_cents) }}</span>
        </div>
      </template>

      <!-- Payment -->
      <div class="opb-row opb-row--payment" data-testid="opb-payment">
        <span class="opb-label">
          {{ hasRefunds ? "Payment after refunds" : "Payment" }}
        </span>
        <span class="opb-value">{{ money(model.payment_cents) }}</span>
      </div>

      <!-- Sales tax the buyer paid: remitted to the state, not profit. -->
      <div
        v-if="model.sales_tax_cents > 0"
        class="opb-row"
        data-testid="opb-sales-tax"
      >
        <span class="opb-label">Sales tax collected (not profit)</span>
        <span class="opb-value">{{ money(model.sales_tax_cents) }}</span>
      </div>

      <!-- Cost group -->
      <div class="opb-group">
        <div class="opb-group-title">Cost</div>
        <div class="opb-row" data-testid="opb-cogs">
          <span class="opb-label">Blank cost (COGS)</span>
          <span class="opb-value">{{ moneyOrPending(model.cost.cogs_cents) }}</span>
        </div>
        <div class="opb-row" data-testid="opb-shipping-carrier">
          <span class="opb-label">Shipping (carrier)</span>
          <span class="opb-value">
            {{ moneyOrPending(model.cost.shipping_carrier_cents) }}
          </span>
        </div>
        <div class="opb-row" data-testid="opb-decoration">
          <span class="opb-label">Decoration</span>
          <span
            v-if="model.cost.decoration_pending"
            class="opb-value opb-value--pending"
          >
            {{ model.ledger_pending ? "Not yet recorded" : "Pending" }}
          </span>
          <span v-else class="opb-value">
            {{ money(model.cost.decoration_cents) }}
            <!-- Embroidery with no recorded stitch count is priced from a
                 size estimate (Jake, 2026-10-22): real money, estimated band. -->
            <span
              v-if="model.cost.decoration_is_estimate"
              class="opb-estimate-tag"
              data-testid="opb-decoration-estimate"
              title="Priced from an estimated stitch count (no digitized design yet)"
            >
              est.
            </span>
          </span>
        </div>
        <div class="opb-row opb-row--subtotal" data-testid="opb-cost-total">
          <span class="opb-label">Total cost</span>
          <span class="opb-value">{{ money(model.cost.total_cents) }}</span>
        </div>
      </div>

      <!-- Fees group -->
      <div class="opb-group">
        <div class="opb-group-title">Fees</div>
        <div class="opb-row" data-testid="opb-shipping-markup">
          <span class="opb-label">Shipping markup</span>
          <span class="opb-value">
            {{ moneyOrPending(model.fees.shipping_markup_cents) }}
          </span>
        </div>
        <div class="opb-row" data-testid="opb-stripe-fee">
          <span class="opb-label">Stripe fee</span>
          <span class="opb-value">
            {{ moneyOrPending(model.fees.stripe_fee_cents) }}
            <span
              v-if="model.fees.stripe_fee_is_estimate"
              class="opb-estimate-tag"
              data-testid="opb-stripe-fee-estimate"
              title="The real fee is known after the charge"
            >
              est.
            </span>
          </span>
        </div>
        <!-- ONE platform fee (the ledger books what checkout charged), shown
             once and subtracted once. -->
        <div class="opb-row" data-testid="opb-tf-platform-fee">
          <span class="opb-label">Platform fee (3.95%)</span>
          <span class="opb-value">
            {{ money(model.fees.tf_platform_fee_cents) }}
          </span>
        </div>
        <!-- ThreadFlow's cut of the decoration fee. It leaves the
             distributor like the platform fees above, so it is a fee and
             comes off profit (decision #3, 2026-10-07). -->
        <div class="opb-row" data-testid="opb-decoration-charge">
          <span class="opb-label">Decoration fee (ThreadFlow)</span>
          <span class="opb-value">
            {{ moneyOrPending(model.fees.decoration_charge_cents, 0, "Not yet recorded") }}
          </span>
        </div>
        <div class="opb-row opb-row--subtotal" data-testid="opb-fees-total">
          <span class="opb-label">Total fees</span>
          <span class="opb-value">{{ money(model.fees.total_cents) }}</span>
        </div>
      </div>

      <!-- Profit -->
      <div
        class="opb-row opb-row--profit"
        :class="{ 'opb-row--loss': model.profit_cents < 0 }"
        data-testid="opb-profit"
      >
        <span class="opb-label">
          {{
            model.ledger_pending
              ? hasUnrecordedItems
                ? "Profit (excl. pending items)"
                : "Profit"
              : model.profit_is_provisional
                ? "Profit (excl. decoration)"
                : "Profit"
          }}
        </span>
        <span class="opb-value">{{ money(model.profit_cents) }}</span>
      </div>
      <!-- Why the costs did not shrink with the refund (card #696). -->
      <div v-if="hasRefunds" class="opb-note" data-testid="opb-refund-note">
        <template v-if="model.blanks_spent">
          Refunds come off profit. The decorator has the order, so the blank
          cost stays as spent, and Stripe keeps its fee on a refund. Shipping,
          decoration and ThreadFlow's fees go down only by what ThreadFlow
          returned with the refund.
        </template>
        <template v-else>
          Refunds come off profit. The order has not gone to the decorator yet, so the
          blank cost goes down with the refund; Stripe keeps its fee on a refund.
          Shipping, decoration and ThreadFlow's fees go down only by what
          ThreadFlow returned with the refund.
        </template>
      </div>
      <div
        v-if="model.ledger_pending"
        class="opb-note"
        data-testid="opb-ledger-pending-note"
      >
        <template v-if="markupUnrecorded || model.cost.decoration_pending">
          No ledger entries are written for this order yet. Figures recorded
          at checkout are shown; shipping markup is Pending and decoration is
          not yet recorded until the ledger is written, and the Stripe fee is
          an estimate until then.
        </template>
        <template v-else>
          No ledger entries are written for this order yet. The figures shown
          were recorded at checkout; the Stripe fee is an estimate until the
          card is charged.
        </template>
      </div>
      <div
        v-else-if="model.profit_is_provisional"
        class="opb-note"
        data-testid="opb-profit-provisional-note"
      >
        Provisional -- decoration cost not yet recorded; profit will decrease
        once the decorator's cost lands.
      </div>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from "@/utils/currency";

export default {
  name: "OrderProfitBreakdown",

  props: {
    /**
     * Pre-fetched breakdown payload (shape returned by GET /orders/:id/split).
     * When provided, the panel renders it directly and does not fetch.
     */
    breakdown: {
      type: Object,
      default: null,
    },
    /**
     * Order id to self-fetch the breakdown for. Used when no `breakdown`
     * prop is passed. Ignored if `breakdown` is provided.
     */
    orderId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      fetched: null,
      loading: false,
      error: null,
      // Monotonic request id -- guards against out-of-order responses when
      // the orderId watcher re-fetches while a prior fetch is still in flight
      // (rapid order-to-order navigation). Only the latest request may write
      // state, so a slow stale response can never overwrite a newer order's
      // cost/margin/profit figures. Mirrors the project inflight-promise-guard
      // pattern.
      reqId: 0,
    };
  },

  computed: {
    model() {
      return this.breakdown || this.fetched;
    },
    // Something was refunded. A payload from before the refund fields
    // (no ``refunded_cents``) reads as no refund and renders as before.
    hasRefunds() {
      return Number(this.model.refunded_cents) > 0;
    },
    // Before the charge: figures the order did NOT record at placement. An
    // order placed since the money freeze (2026-10-22) recorded its shipping
    // markup and decoration, so they are numbers and not pending; an older
    // order did not, and they stay pending (never re-derived from the plan).
    markupUnrecorded() {
      return this.model.fees.shipping_markup_cents == null;
    },
    hasUnrecordedItems() {
      return (
        this.markupUnrecorded ||
        Boolean(this.model.cost.decoration_pending) ||
        this.model.cost.cogs_cents == null
      );
    },
  },

  watch: {
    // Re-fetch when the order id changes (e.g. navigating between orders).
    orderId() {
      if (!this.breakdown) {
        this.loadBreakdown();
      }
    },
  },

  mounted() {
    // Self-fetch only when we were not handed a breakdown and we have an id.
    if (!this.breakdown && this.orderId != null) {
      this.loadBreakdown();
    }
  },

  methods: {
    money(cents) {
      // Wire values are integer cents; the shared helper formats dollars.
      if (cents === null || cents === undefined) return formatCurrency(null);
      return formatCurrency(Number(cents) / 100);
    },

    // An unrecorded figure (null) is "Pending", never $0.00: zero reads as
    // "costs nothing". ``absent`` is the value for a payload that predates
    // the field entirely (undefined), which keeps its old rendering.
    moneyOrPending(cents, absent, nullLabel = "Pending") {
      if (cents === null) return nullLabel;
      if (cents === undefined && absent !== undefined) return this.money(absent);
      return this.money(cents);
    },

    async loadBreakdown() {
      if (this.orderId == null) return;
      const reqId = ++this.reqId; // claim the latest request
      this.loading = true;
      this.error = null;
      try {
        const resp = await this.$axios.get(`/orders/${this.orderId}/split`);
        if (reqId !== this.reqId) return; // superseded by a newer fetch
        this.fetched = resp && resp.data ? resp.data : null;
      } catch (e) {
        if (reqId !== this.reqId) return;
        this.error = "Unable to load the profit breakdown for this order.";
        this.fetched = null;
      } finally {
        // Only the latest request clears the spinner, so an early-returning
        // stale call cannot flip loading off while the current fetch runs.
        if (reqId === this.reqId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.order-profit-breakdown {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 16px;
}

.opb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.opb-internal-badge {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: 2px 6px;
  color: rgba(0, 0, 0, 0.6);
}

.opb-estimate-tag {
  font-size: 0.7rem;
  text-transform: uppercase;
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.55);
}

.opb-group {
  margin-top: 12px;
}

.opb-group-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 4px;
}

.opb-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
}

.opb-row--subtotal {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 4px;
  padding-top: 6px;
  font-weight: 600;
}

.opb-row--payment {
  font-weight: 600;
}

.opb-row--profit {
  border-top: 2px solid rgba(0, 0, 0, 0.16);
  margin-top: 12px;
  padding-top: 8px;
  font-weight: 700;
  font-size: 1.05rem;
}

.opb-row--loss .opb-value {
  color: #c62828;
}

.opb-value--pending {
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
}

.opb-note {
  margin-top: 6px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  font-style: italic;
}
</style>
